import {
  createGrid,
  GridApi,
  GridOptions,
  ColDef,
  IRowNode,
  SelectionChangedEvent,
  ProcessCellForExportParams,
  ProcessRowGroupForExportParams,
  CsvCustomContent,
  ExcelRow,
  DefaultMenuItem,
  MenuItemDef,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../ag_grid_base_component/ag_grid_base_controller";
import { StatusCellRenderer } from "../ag_grid_shared/cell_renderers/status_cell_renderer";
import { ActionsCellRenderer } from "../ag_grid_shared/cell_renderers/actions_cell_renderer";
import { DropdownActionsCellRenderer } from "../ag_grid_shared/cell_renderers/dropdown_actions_cell_renderer";
import { LinkCellRenderer } from "../ag_grid_shared/cell_renderers/link_cell_renderer";
import { PostLinkCellRenderer } from "../ag_grid_shared/cell_renderers/post_link_cell_renderer";
import { MultilineTextCellRenderer } from "../ag_grid_shared/cell_renderers/multiline_text_cell_renderer";
import { MaskedTextCellRenderer } from "../ag_grid_shared/cell_renderers/masked_text_cell_renderer";
import { IconBadgeCellRenderer } from "../ag_grid_shared/cell_renderers/icon_badge_cell_renderer";
import { ElectionNameCellRenderer } from "../ag_grid_shared/cell_renderers/election_name_cell_renderer";
import { StatusTestFilter, statusTestFilterHandler } from "../ag_grid_shared/filters/status_test_filter";
import { ebTheme } from "../ag_grid_shared/eb_theme";
import { ebFilterParams, ebLocaleText } from "../ag_grid_shared/filter_defaults";
import { withDateFilterComparator } from "../ag_grid_shared/filters/date_string_comparator";
import { withNumberComparator } from "../ag_grid_shared/sorters/number_comparator";
import { withQuickFilterFields } from "../ag_grid_shared/filters/quick_filter_scope";
import { formatCellValue, formatGroupCellValue } from "../ag_grid_shared/export_utils";
import { showConfirmModal, ConfirmModalOptions } from "../ag_grid_shared/confirm_modal";

export default class extends AgGridBaseController {
  static targets = [
    ...AgGridBaseController.targets,
    "quickFilterInput",
  ];

  static values = {
    ...AgGridBaseController.values,
    rowDataUrl:            String,
    rowData:               Array,
    advancedRowDataUrl:    String,
    basicModeOptions:      Object,
    advancedModeOptions:   Object,
    footerCounts:          Object,
    rowReorderUrl:         String,
    exportTitle:           String,
    exportFileName:        String,
    exportUrl:             String,
    quickFilterFields:     Array,
  };

  declare readonly quickFilterInputTarget: HTMLInputElement;
  declare readonly hasQuickFilterInputTarget: boolean;

  declare readonly rowDataUrlValue: string;
  declare readonly rowDataValue: Record<string, unknown>[];
  declare readonly advancedRowDataUrlValue: string;
  declare readonly basicModeOptionsValue: Partial<GridOptions>;
  declare readonly advancedModeOptionsValue: Partial<GridOptions>;
  declare readonly footerCountsValue: {
    labelField?: string;
    sumFields?: string[];
    countLabel?: string;
  };
  // Empty string when absent, which every check below treats as falsy, so
  // existing consumers are byte-for-byte unchanged.
  declare readonly rowReorderUrlValue: string;
  declare readonly exportTitleValue: string;
  declare readonly exportFileNameValue: string;
  // Set only by grids whose export is built server-side; empty keeps the client-side download.
  declare readonly exportUrlValue: string;
  // Row-data keys the quick filter is allowed to match. Empty (the default) keeps AG Grid's
  // stock every-column search, so existing consumers are unchanged.
  declare readonly quickFilterFieldsValue: string[];

  private headerContextMenuBlocker: ((e: MouseEvent) => void) | null = null;
  private hasActiveRange = false;
  private totalRowCount = 0;
  // Incremented each time a new data fetch starts so in-flight responses can
  // detect they've been superseded (e.g., mode switch during load).
  private dataFetchGeneration = 0;
  // Holds AG Grid state to apply after Advanced column defs + row data finish loading.
  private pendingRestoreState: ReturnType<GridApi["getState"]> | null = null;
  // Opt-in `grid:refresh` listener (spec 011 Decision C), scoped to this element so each
  // grid on the page refreshes only itself. Arrow property for add/removeEventListener symmetry.
  private handleGridRefresh = (): void => {
    this.refreshRowData();
  };

  connect() {
    super.connect(); // license key + outside click handler

    this.element.addEventListener("grid:refresh", this.handleGridRefresh);

    const hasAdvanced = this.advancedColumnDefsValue.length > 0;
    const sideBarConfig = this.buildSideBarConfig(hasAdvanced);
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};

    const options: GridOptions = {
      theme: ebTheme,
      localeText: ebLocaleText,
      columnDefs: this.decorateColumnDefs(
        this.withRankComparator(
          this.withActionsValueFormatter(
            hasAdvanced ? this.basicColDefs(this.columnDefsValue) : this.columnDefsValue
          )
        )
      ),
      sideBar: sideBarConfig,
      enableFilterHandlers: true,
      autoGroupColumnDef: {
        comparator: (valueA: unknown, valueB: unknown, nodeA: IRowNode, nodeB: IRowNode) => {
          const field = nodeA.field ?? "";
          const colDef = field ? this.gridApi?.getColumn(field)?.getColDef() : null;
          if ((colDef?.context as Record<string, unknown>)?.["countSort"]) {
            return (nodeA.allLeafChildren?.length ?? 0) - (nodeB.allLeafChildren?.length ?? 0);
          }
          return String(valueA ?? "").localeCompare(String(valueB ?? ""));
        },
      },
      components: {
        statusCellRenderer: StatusCellRenderer,
        actionsCellRenderer: ActionsCellRenderer,
        dropdownActionsCellRenderer: DropdownActionsCellRenderer,
        linkCellRenderer: LinkCellRenderer,
        postLinkCellRenderer: PostLinkCellRenderer,
        multilineTextCellRenderer: MultilineTextCellRenderer,
        maskedTextCellRenderer: MaskedTextCellRenderer,
        iconBadgeCellRenderer: IconBadgeCellRenderer,
        electionNameCellRenderer: ElectionNameCellRenderer,
        // The SSRM controller already registered this; CSRM grids referencing the key had
        // a dead filter until now. Inert for every consumer whose colDefs don't reference it.
        statusTestFilter: StatusTestFilter,
      },
      // Matches the model against serialized status_label/test/row_type (see
      // status_test_filter.ts); only invoked when a colDef uses the filter.
      filterHandlers: {
        statusTestFilter: statusTestFilterHandler,
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        this.handleSelectionChanged(event);
      },
      onCellSelectionChanged: () => {
        this.handleRangeSelectionChanged();
      },
      onColumnRowGroupChanged: () => {
        this.autoSizeNonFlexColumns();
      },
      onRowGroupOpened: () => {
        this.autoSizeNonFlexColumns();
      },
      onModelUpdated: () => {
        if (this.pendingAutoSize) {
          this.pendingAutoSize = false;
          this.autoSizeNonFlexColumns();
        }
        this.refreshFooterRow();
      },
      onStateUpdated: () => {
        this.saveGridState();
      },
      getMainMenuItems: (params) => this.withPivotAwareResetColumns(params),
      // Only overridden for a server-exporting grid: AG Grid's stock Export items call the grid API
      // directly, so leaving them would keep a second, client-side export next to the standardized
      // one — the very mismatch export_url exists to remove. Grids without export_url keep the
      // stock menu untouched.
      getContextMenuItems: this.exportUrlValue
        ? (params) => this.withServerExportMenuItems(params.defaultItems ?? [])
        : undefined,
      processCellForClipboard: (params: ProcessCellForExportParams) => formatCellValue(params),
      // `undefined` when absent, identical to the key never being set, so existing
      // consumers are unaffected.
      onRowDragEnd: this.rowReorderUrlValue ? () => this.persistRowOrder() : undefined,
      // G5 (HARD): a column filter hides rows like search text does, so the drag handle
      // must be suppressed while ANY filter is active, not just quick-filter text.
      onFilterChanged: this.rowReorderUrlValue ? () => this.syncRowDragSuppression() : undefined,
      ...this.gridOptionsValue,
      // Placed after the gridOptionsValue spread so the shared filterParams default
      // survives even though each page supplies its own defaultColDef via Ruby.
      defaultColDef: { filterParams: ebFilterParams, ...baseColDef },
    };

    // getDataPath can't come through as JSON from Ruby, so default it here when opted in.
    if (this.gridOptionsValue?.treeData && !options.getDataPath) {
      options.getDataPath = (data: Record<string, unknown>) => data.group_path as string[];
    }

    const rowSel = options.rowSelection as Record<string, unknown> | undefined;
    if (rowSel && typeof rowSel === "object") {
      rowSel.isRowSelectable = this.rowSelectableCallback;
    }

    if (!this.hasGridTarget) return;

    this.gridApi = createGrid(this.gridTarget, options);
    this.syncDefaultExportParams();
    this.setupToolPanelHeaders();

    const isAutoHeight = this.gridOptionsValue?.domLayout === "autoHeight";
    if (!isAutoHeight) {
      this.initDynamicHeight();
    }

    this.loadGridState();

    this.setupFollowSelection();

    if (hasAdvanced) {
      this.isBasicMode = true;
      this.setBasicModeColumnOptions();
      this.headerContextMenuBlocker = (e: MouseEvent) => {
        if (this.isBasicMode && (e.target as HTMLElement).closest(".ag-header")) {
          e.stopImmediatePropagation();
          e.preventDefault();
        }
      };
      this.gridTarget.addEventListener("contextmenu", this.headerContextMenuBlocker, true);
    }

    if (this.rowDataUrlValue) {
      this.fetchRowData(this.rowDataUrlValue);
    } else if (this.rowDataValue.length > 0) {
      this.pendingAutoSize = true;
      this.gridApi.setGridOption("rowData", this.rowDataValue);
    }

    this.gridBlurHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (this.gridTarget.contains(target)) return;
      const exportWrapper = this.hasExportToggleTarget
        ? this.exportToggleTarget.closest(".ag-grid-component-export-wrapper")
        : null;
      if (exportWrapper?.contains(target)) return;
      this.gridApi?.clearCellSelection();
      this.hasActiveRange = false;
      this.syncDefaultExportParams();
    };
    document.addEventListener("mousedown", this.gridBlurHandler);
  }

  disconnect() {
    // Clean up CSRM-only state before base destroys the grid
    this.pendingRestoreState = null;
    if (this.headerContextMenuBlocker && this.hasGridTarget) {
      this.gridTarget.removeEventListener("contextmenu", this.headerContextMenuBlocker, true);
      this.headerContextMenuBlocker = null;
    }
    this.element.removeEventListener("grid:refresh", this.handleGridRefresh);
    super.disconnect(); // resize/click/mousedown listeners + gridApi.destroy()
  }

  // Swaps only rowData (never columnDefs, unlike fetchQuestionData), so
  // sort/filter/scroll survive a poll-driven `grid:refresh`.
  refreshRowData(): void {
    if (!this.gridApi || !this.rowDataUrlValue) return;
    const generation = ++this.dataFetchGeneration;
    fetch(this.rowDataUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to refresh row data: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (generation !== this.dataFetchGeneration || !this.gridApi) return;
        const rows = Array.isArray(json) ? json : (json.rows ?? []);
        this.gridApi.setGridOption("rowData", rows);
      })
      .catch((error) => {
        if (generation !== this.dataFetchGeneration) return;
        console.error("[AgGridComponent] refreshRowData error:", error);
      });
  }

  onFilterTextBoxChanged() {
    if (!this.gridApi || !this.hasQuickFilterInputTarget) return;
    const filterText = this.quickFilterInputTarget.value;
    this.gridApi.setGridOption("quickFilterText", filterText);
    // G5 (HARD): a full-list persisted order captured while filtered would silently
    // drop the hidden rows' positions, so disable the drag handle while filtered.
    this.syncRowDragSuppression();
  }

  // Called from both the search box and column-filter changes; no-op for grids
  // without reorder wired.
  private syncRowDragSuppression(): void {
    if (!this.gridApi || !this.rowReorderUrlValue) return;
    this.gridApi.setGridOption("suppressRowDrag", this.reorderUnsafe());
  }

  private reorderUnsafe(): boolean {
    const searchActive =
      this.hasQuickFilterInputTarget && this.quickFilterInputTarget.value.trim().length > 0;
    return searchActive || (this.gridApi?.isAnyFilterPresent() ?? false);
  }

  // Collects the CURRENT displayed row order (post-drag, post-filter-and-sort)
  // and POSTs it to the caller-supplied URL.
  private persistRowOrder(): void {
    if (!this.gridApi || !this.rowReorderUrlValue) return;

    // G5 backstop: even if suppressRowDrag somehow didn't prevent the drag, never
    // persist a filtered subset as the full order.
    if (this.reorderUnsafe()) return;

    const ids: (string | number)[] = [];
    this.gridApi.forEachNodeAfterFilterAndSort((node) => {
      const id = (node.data as Record<string, unknown> | undefined)?.id;
      if (id !== undefined && id !== null) ids.push(id as string | number);
    });
    if (ids.length === 0) return;

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    fetch(this.rowReorderUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify({ votes_in_order: ids, offset: 0 }),
    }).catch((error) => {
      console.error("[AgGridComponent] persistRowOrder error:", error);
    });
  }

  switchToBasic() {
    if (!this.gridApi) return;
    this.isBasicMode = true;
    this.gridApi.setGridOption("loading", true);
    this.setActiveToggle("basic");
    this.setBasicModeColumnOptions();
    if (this.hasFilterToggleTarget) {
      this.gridApi.closeToolPanel();
      this.filterToggleTarget.style.display = "none";
      this.filterToggleTarget.setAttribute("aria-pressed", "false");
    }
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.style.display = "none";
      this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    }
    if (this.hasExportToggleTarget) {
      this.closeExportDropdown();
    }
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.syncDefaultExportParams();
    this.gridApi?.clearCellSelection();
    this.applyModeColumnDefs(this.rowDataUrlValue, this.basicColDefs(this.columnDefsValue));
    // `mode` is not part of AG Grid's state, so a mode flip alone may not emit onStateUpdated.
    this.saveGridState();
  }

  switchToAdvanced() {
    if (!this.gridApi) return;
    this.isBasicMode = false;
    this.gridApi.setGridOption("loading", true);
    this.setActiveToggle("advanced");
    this.setAdvancedModeColumnOptions();
    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.style.display = "";
    }
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.style.display = "";
    }
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.syncDefaultExportParams();
    this.gridApi?.clearCellSelection();
    this.applyModeColumnDefs(this.advancedRowDataUrlValue, this.advancedColumnDefsValue);
    this.saveGridState();
  }

  // unknown, not string: this is also bound directly to the Export button's click handler, which passes a MouseEvent
  exportCsv(titleOverride?: unknown) {
    const title = this.resolveExportTitle(titleOverride);
    if (this.hasActiveRange) {
      this.exportRangeData("csv", title);
      return;
    }
    if (this.exportUrlValue) {
      void this.triggerServerExport("csv");
      return;
    }
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    const processRowGroupCallback = (params: ProcessRowGroupForExportParams) => formatGroupCellValue(params);
    const prependContent = this.csvPrependContent(title);
    const fileName = this.resolveExportFileName("csv");
    if (this.hasRowSelection) {
      this.gridApi?.exportDataAsCsv({ ...this.buildSelectionExportParams(), processCellCallback, processRowGroupCallback, prependContent, fileName });
    } else {
      this.gridApi?.exportDataAsCsv({ processCellCallback, processRowGroupCallback, prependContent, fileName });
    }
    this.closeExportDropdown();
  }

  exportExcel(titleOverride?: unknown) {
    const title = this.resolveExportTitle(titleOverride);
    if (this.hasActiveRange) {
      this.exportRangeData("excel", title);
      return;
    }
    if (this.exportUrlValue) {
      void this.triggerServerExport("xlsx");
      return;
    }
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    const processRowGroupCallback = (params: ProcessRowGroupForExportParams) => formatGroupCellValue(params);
    const prependContent = this.excelPrependContent(title);
    const fileName = this.resolveExportFileName("xlsx");
    if (this.hasRowSelection) {
      this.gridApi?.exportDataAsExcel({ ...this.buildSelectionExportParams(), processCellCallback, processRowGroupCallback, prependContent, fileName });
    } else {
      this.gridApi?.exportDataAsExcel({ processCellCallback, processRowGroupCallback, prependContent, fileName });
    }
    this.closeExportDropdown();
  }

  // Posts the same payload shape the SSRM grid sends (format/mode/row_ids/search_text/advanced) so
  // one backend serves both, and renders the Turbo Stream the server replies with — the report
  // itself arrives later as a toast, built by the generator rather than by AG Grid.
  private async triggerServerExport(format: "csv" | "xlsx") {
    this.closeExportDropdown();

    const selectedIds = this.selectedRowIds();
    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    try {
      const response = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          format,
          mode: selectedIds.length > 0 ? "rows" : "all",
          row_ids: selectedIds.length > 0 ? selectedIds : undefined,
          search_text: this.hasQuickFilterInputTarget ? this.quickFilterInputTarget.value.trim() || undefined : undefined,
          advanced: !this.isBasicMode,
        }),
      });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const html = await response.text();
      if (html.trim()) window.Turbo?.renderStreamMessage(html);
    } catch (error) {
      console.error("[AgGridComponent] export error:", error);
      alert("Export failed. Please try again.");
    }
  }

  private withServerExportMenuItems(
    defaultItems: (DefaultMenuItem | MenuItemDef)[]
  ): (DefaultMenuItem | MenuItemDef)[] {
    const items = defaultItems.filter((item) => item !== "export");
    items.push({
      name: "Export",
      icon: '<span class="ag-icon ag-icon-save"></span>',
      subMenu: [
        { name: "CSV Export", icon: '<span class="ag-icon ag-icon-csv"></span>', action: () => this.exportCsv() },
        { name: "Excel Export", icon: '<span class="ag-icon ag-icon-excel"></span>', action: () => this.exportExcel() },
      ],
    });
    return items;
  }

  private selectedRowIds(): string[] {
    if (!this.hasRowSelection || !this.gridApi) return [];

    return this.gridApi
      .getSelectedNodes()
      .map((node) => (node.data as Record<string, unknown> | undefined)?.id)
      .filter((id): id is string | number => id != null)
      .map(String);
  }

  // Generic confirm-then-POST for server-side async exports; reached via Stimulus outlet since
  // app/javascript can't import app/components modules directly (tsconfig rootDir).
  async confirmAndPost(url: string, options: ConfirmModalOptions): Promise<boolean> {
    if (!url) return false;
    const ok = await showConfirmModal(options);
    if (!ok) return false;

    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return true;
    } catch (error) {
      console.error("[AgGridComponent] confirmAndPost error:", error);
      alert("Export failed. Please try again.");
      return false;
    }
  }

  // Explicit caller-supplied title (e.g. vote-by-vote's question name) wins; otherwise falls
  // back to the component-level exportTitleValue (e.g. election name), if any.
  private resolveExportTitle(explicit?: unknown): string | undefined {
    if (typeof explicit === "string" && explicit) return explicit;
    return this.exportTitleValue || undefined;
  }

  private resolveExportFileName(format: "csv" | "xlsx"): string {
    const base = this.exportFileNameValue || this.slugify(this.exportTitleValue) || "export";
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}`;
    return `${base}-${stamp}.${format}`;
  }

  private slugify(value: string): string {
    return (value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  // CSV prependContent is built as cells (not a raw string) so AG Grid applies its own
  // CSV escaping/quoting to the question name.
  private csvPrependContent(title: string | undefined): CsvCustomContent | undefined {
    return title ? [[{ data: { value: title } }]] : undefined;
  }

  private excelPrependContent(title: string | undefined): ExcelRow[] | undefined {
    return title ? [{ cells: [{ data: { type: "String", value: title } }] }] : undefined;
  }

  // Resets sort/pin/filter state so it doesn't carry over onto the new question's same-named column.
  fetchQuestionData(url: string): void {
    if (!this.gridApi) return;
    this.gridApi.setGridOption("loading", true);
    this.hasActiveRange = false;
    this.hasRowSelection = false;
    this.syncDefaultExportParams();
    this.gridApi.clearCellSelection();
    this.gridApi.setFilterModel(null);
    this.gridApi.resetColumnState();
    if (this.hasQuickFilterInputTarget) {
      this.quickFilterInputTarget.value = "";
      this.gridApi.setGridOption("quickFilterText", "");
    }
    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch question data: ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (!this.gridApi) return;
        this.pendingAutoSize = true;
        this.gridApi.setGridOption("columnDefs", this.decorateColumnDefs(json.column_defs ?? []));
        this.gridApi.setGridOption("rowData", json.rows ?? []);
        this.gridApi.setGridOption("pinnedBottomRowData", json.footer_row ?? []);
        this.gridApi.setGridOption("loading", false);
      })
      .catch((error) => {
        console.error("[AgGridComponent] fetchQuestionData error:", error);
        this.gridApi?.setGridOption("loading", false);
      });
  }

  private setBasicModeColumnOptions() {
    if (!this.gridApi) return;
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { filterParams: ebFilterParams, ...baseColDef, suppressHeaderMenuButton: true });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", true);
    if (Object.keys(this.basicModeOptionsValue).length > 0) {
      this.gridApi.updateGridOptions(this.basicModeOptionsValue);
    }
  }

  private setAdvancedModeColumnOptions() {
    if (!this.gridApi) return;
    const baseColDef = (this.gridOptionsValue.defaultColDef as Record<string, unknown>) ?? {};
    this.gridApi.setGridOption("defaultColDef", { filterParams: ebFilterParams, ...baseColDef });
    this.gridApi.setGridOption("suppressDragLeaveHidesColumns", false);
    if (Object.keys(this.advancedModeOptionsValue).length > 0) {
      this.gridApi.updateGridOptions(this.advancedModeOptionsValue);
    }
  }

  private handleRangeSelectionChanged() {
    const ranges = this.gridApi?.getCellRanges();
    if (!ranges || ranges.length === 0) {
      this.hasActiveRange = false;
      this.syncDefaultExportParams();
      return;
    }
    const range = ranges[0];
    const startRow = range.startRow?.rowIndex ?? 0;
    const endRow = range.endRow?.rowIndex ?? 0;
    const rowSpan = Math.abs(endRow - startRow) + 1;
    this.hasActiveRange = rowSpan > 1 || range.columns.length > 1;
    this.syncDefaultExportParams();
  }

  // Columns/rows to restrict an export to, based on the active cell range.
  // Shared by exportRangeData and syncDefaultExportParams.
  private buildRangeExportParams(): {
    columnKeys: string[];
    shouldRowBeSkipped: (params: { node: IRowNode }) => boolean;
  } | null {
    if (!this.gridApi) return null;
    const ranges = this.gridApi.getCellRanges();
    if (!ranges || ranges.length === 0) return null;
    const range = ranges[0];
    const startIsPinned = range.startRow!.rowPinned === "bottom";
    const endIsPinned = range.endRow!.rowPinned === "bottom";
    const includeFooter = startIsPinned || endIsPinned;
    const columnKeys = range.columns.map((col) => col.getColId());

    let startRow: number;
    let endRow: number;

    if (startIsPinned && endIsPinned) {
      startRow = 0;
      endRow = -1;
    } else if (endIsPinned) {
      startRow = range.startRow!.rowIndex;
      endRow = this.gridApi.getDisplayedRowCount() - 1;
    } else if (startIsPinned) {
      startRow = 0;
      endRow = range.endRow!.rowIndex;
    } else {
      const rawStart = range.startRow!.rowIndex;
      const rawEnd = range.endRow!.rowIndex;
      startRow = Math.min(rawStart, rawEnd);
      endRow = Math.max(rawStart, rawEnd);
    }

    return {
      columnKeys,
      shouldRowBeSkipped: ({ node }: { node: IRowNode }) => {
        if (node.rowPinned === "bottom") return !includeFooter;
        const idx = node.rowIndex ?? -1;
        return idx < startRow || idx > endRow;
      },
    };
  }

  // Replicates onlySelected's row filtering, but exempts the footer — AG Grid's
  // own onlySelected always drops pinned rows since they can never be "selected".
  private buildSelectionExportParams(): {
    shouldRowBeSkipped: (params: { node: IRowNode }) => boolean;
  } {
    return {
      shouldRowBeSkipped: ({ node }: { node: IRowNode }) => {
        if (node.rowPinned === "bottom") return false;
        return !node.isSelected();
      },
    };
  }

  // Keeps default export params in sync so the native right-click "Export" menu
  // (which bypasses exportCsv/exportExcel entirely) also respects selection.
  private syncDefaultExportParams() {
    if (!this.gridApi) return;
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    const processRowGroupCallback = (params: ProcessRowGroupForExportParams) => formatGroupCellValue(params);

    let scopeParams: Record<string, unknown> = {};
    if (this.hasActiveRange) {
      scopeParams = this.buildRangeExportParams() ?? {};
    } else if (this.hasRowSelection) {
      scopeParams = this.buildSelectionExportParams();
    }

    // fileName too: without it the native menu's download is AG Grid's stock export.csv/export.xlsx
    // even on grids whose toolbar button is correctly named.
    const params = { processCellCallback, processRowGroupCallback, ...scopeParams };
    this.gridApi.setGridOption("defaultCsvExportParams", { ...params, fileName: this.resolveExportFileName("csv") });
    this.gridApi.setGridOption("defaultExcelExportParams", { ...params, fileName: this.resolveExportFileName("xlsx") });
  }

  private exportRangeData(format: "csv" | "excel", title?: string) {
    if (!this.gridApi) return;
    const rangeParams = this.buildRangeExportParams();
    if (!rangeParams) return;
    const processCellCallback = (params: ProcessCellForExportParams) => formatCellValue(params);
    const processRowGroupCallback = (params: ProcessRowGroupForExportParams) => formatGroupCellValue(params);

    if (format === "csv") {
      const prependContent = this.csvPrependContent(title);
      this.gridApi.exportDataAsCsv({ ...rangeParams, processCellCallback, processRowGroupCallback, prependContent, fileName: this.resolveExportFileName("csv") });
    } else {
      const prependContent = this.excelPrependContent(title);
      this.gridApi.exportDataAsExcel({ ...rangeParams, processCellCallback, processRowGroupCallback, prependContent, fileName: this.resolveExportFileName("xlsx") });
    }
    this.closeExportDropdown();
  }

  private async loadGridState() {
    const state = await this.fetchGridState();
    if (!state || !this.gridApi) return;

    const { mode, ...agState } = state;
    if (Object.keys(agState).length === 0) return;

    const hasAdvanced = this.advancedColumnDefsValue.length > 0;
    this.isRestoringState = true;
    if (mode === "advanced" && hasAdvanced) {
      // Advanced needs its column defs and row data in place before state applies, so hand off to
      // applyPendingRestoreState once switchToAdvanced's fetch lands.
      this.pendingRestoreState = agState as ReturnType<GridApi["getState"]>;
      this.switchToAdvanced();
      return;
    }
    this.gridApi.setState(agState as ReturnType<GridApi["getState"]>);
    requestAnimationFrame(() => {
      this.isRestoringState = false;
      this.syncPanelButtonStates();
    });
  }

  private applyPendingRestoreState() {
    if (!this.pendingRestoreState || !this.gridApi) return;
    const state = this.pendingRestoreState;
    this.pendingRestoreState = null;
    this.gridApi.setState(state);
    requestAnimationFrame(() => {
      this.isRestoringState = false;
      this.syncPanelButtonStates();
    });
  }

  private fetchRowData(url: string) {
    this.fetchRowDataWithColumnDefs(url, null);
  }

  // Single decoration point for every columnDefs assignment, so the quick-filter scope
  // survives the view toggle and every mid-flight refetch — not just the initial mount.
  private decorateColumnDefs(columnDefs: ColDef[]): ColDef[] {
    return withQuickFilterFields(
      withNumberComparator(withDateFilterComparator(columnDefs)),
      this.quickFilterFieldsValue
    );
  }

  private applyModeColumnDefs(url: string, columnDefs: ColDef[]): void {
    if (url) {
      this.fetchRowDataWithColumnDefs(url, columnDefs);
      return;
    }
    if (!this.gridApi) return;
    this.pendingAutoSize = true;
    this.gridApi.setGridOption("columnDefs", this.decorateColumnDefs(columnDefs));
    this.gridApi.setGridOption("loading", false);
    this.applyPendingRestoreState();
  }

  private fetchRowDataWithColumnDefs(url: string, columnDefs: ColDef[] | null) {
    if (!url) {
      console.error("[AgGridComponent] fetchRowDataWithColumnDefs called without a url");
      this.gridApi?.setGridOption("loading", false);
      this.pendingRestoreState = null;
      this.isRestoringState = false;
      return;
    }
    const generation = ++this.dataFetchGeneration;
    fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch row data: ${response.status}`);
        }
        return response.json();
      })
      .then((json) => {
        if (generation !== this.dataFetchGeneration || !this.gridApi) return;
        const rows = Array.isArray(json) ? json : (json.rows ?? []);
        if (columnDefs !== null) {
          this.gridApi.setGridOption("columnDefs", this.decorateColumnDefs(columnDefs));
        } else if (!Array.isArray(json) && json.column_defs) {
          this.gridApi.setGridOption("columnDefs", this.decorateColumnDefs(json.column_defs));
        }
        this.totalRowCount = rows.length;
        this.pendingAutoSize = true;
        this.gridApi.setGridOption("rowData", rows);
        const hasFooterConfig = !!(this.footerCountsValue.labelField || this.footerCountsValue.sumFields?.length);
        if (!hasFooterConfig) {
          this.gridApi.setGridOption(
            "pinnedBottomRowData",
            Array.isArray(json) ? [] : (json.footer_row ?? [])
          );
        }
        this.gridApi.setGridOption("loading", false);
        this.applyPendingRestoreState();
      })
      .catch((error) => {
        if (generation !== this.dataFetchGeneration) return;
        console.error("[AgGridComponent] fetchRowData error:", error);
        this.gridApi?.setGridOption("loading", false);
        this.pendingRestoreState = null;
        this.isRestoringState = false;
      });
  }

  private handleSelectionChanged(event: SelectionChangedEvent) {
    const selectedRows = event.api.getSelectedRows() as Array<Record<string, unknown>>;
    const count = selectedRows.length;
    this.hasRowSelection = count > 0;
    this.syncDefaultExportParams();

    if (!this.hasActionsMenuTarget) return;

    if (count > 0) {
      this.actionsMenuTarget.classList.add("visible");
      this.actionsMenuTarget.style.display = ""; // clear any leftover hide from scroll-tracking
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = `${count} selected`;
      }
      this.renderActions(selectedRows);
      if (this.followSelectionValue) {
        this.updateFollowScrollVisibility();
      }
    } else {
      this.actionsMenuTarget.classList.remove("visible");
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = "";
      }
      this.clearActions();
      if (this.followSelectionValue) {
        this.resetMenuPosition();
      }
    }
  }

  private refreshFooterRow(): void {
    if (!this.gridApi) return;
    const config = this.footerCountsValue;
    if (!config.labelField && !config.sumFields?.length) return;

    const displayedCount = this.gridApi.getDisplayedRowCount();
    const footerRow: Record<string, unknown> = {};

    if (config.labelField) {
      const label = config.countLabel ?? "rows";
      footerRow[config.labelField] =
        displayedCount === this.totalRowCount
          ? `${this.totalRowCount} ${label}`
          : `${displayedCount} of ${this.totalRowCount} ${label}`;
    }

    if (config.sumFields && config.sumFields.length > 0) {
      const sums: Record<string, number> = {};
      config.sumFields.forEach((f: string) => { sums[f] = 0; });
      this.gridApi.forEachNodeAfterFilter((node) => {
        if (!node.data) return;
        config.sumFields!.forEach((f: string) => {
          sums[f] += Number((node.data as Record<string, unknown>)[f] ?? 0);
        });
      });
      Object.assign(footerRow, sums);
    }

    this.gridApi.setGridOption("pinnedBottomRowData", [footerRow]);
  }
}
